import { zodResolver } from "@hookform/resolvers/zod";
import type { ColumnDef, Getter } from "@tanstack/react-table";
import type { AxiosError } from "axios";
import Axios from "axios";
import type { ChangeEvent } from "react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import styled, { useTheme } from "styled-components/macro";
import useSWR from "swr";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { z } from "zod";
import { useAuthContext } from "../../../../../../components/Auth";
import { Breadcrumb } from "../../../../../../components/ProductsLayout/SharedProductsLayout";
import { LinkUnstyled } from "../../../../../../components/LinkUnstyled/LinkUnstyled";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
  DestructiveButtonLargeV2,
  LinkButtonLargeV2,
  TertiaryButtonMediumV2,
} from "../../../../../../components/Buttons/Buttons";
import { CheckBoxNoLabel } from "../../../../../../components/CheckBoxes/CheckBoxes";
import { DelayedSpinner } from "../../../../../../components/DelayedSpinner/DelayedSpinner";
import { EditableTitle } from "../../../../../../components/EditableTitle/EditableTitle";
import { ErrorPlaceholder } from "../../../../../../components/Error";
import {
  CaretLeft,
  DeleteIcon,
  InfoIcon,
  LoadingIcon,
  PlusIcon,
  SystemDefaultIcon,
} from "../../../../../../components/Icons/Icons";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";
import { SlideOut } from "../../../../../../components/SlideOut/SlideOut";
import { Table } from "../../../../../../components/Table/Table";
import {
  H3,
  SoftHeader2,
  SoftHeaderMedium,
} from "../../../../../../components/Typography/Typography";
import { endpoints } from "../../../../../../endpoints";
import { Flex } from "../../../../../../layout/FormLayout";
import {
  PageWrapper,
  WhiteInnerCard,
} from "../../../../../../layout/portalPageLayout";
import { ContentWrapper } from "../../../../../../layout/publicPageLayout";
import type { TranslationsSchema, UUID } from "../../../../../../types/types";
import type {
  AttributeSchema,
  AttributeTemplateSchema,
  CollectionPatchArgsSchema,
  CollectionSchema,
} from "../../../../../../types/types.PIM";
import { useRoutePath } from "../../../../../../util/Routing";
import { isSystemProtectedAttribute } from "../../../../../../util/attributeEditability";
import { useDebouncedRowReorder } from "../../../../../../util/hooks";
import {
  removeUnderscore,
  rowHover,
  toTitleCase,
  useFormWrapper,
  useHasMultipleLanguages,
  useStoreState,
} from "../../../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../../../util/mdmEntityDisplayNames";
import { zodRequiredString } from "../../../../../../util/zod.util";
import { AttributesNav } from "../../components/AttributesNav";
import {
  PageContentWrapper,
  SystemIconWrapper,
} from "../../components/PIM.components.util";
import { TemplateCollectionItems } from "../../SellerAdminTemplates/TemplateCollectionItems";
import {
  TemplatesNav,
  TemplatesNavWithPadding,
} from "../../SellerAdminTemplates/TemplatesNav";
import { CollectionTranslations } from "../CollectionTranslations";
import {
  AddAttributeToExistingCollectionForm,
  attributeToCollectionColumnCreation,
  collectionRowToCollectionRowCreation,
} from "./AddAttributeToExistingCollectionForm";
import { ReferenceEntityMappingTable } from "../../../../../../components/ReferenceEntityMappingTable";
import { shouldShowObjectReferenceAttribute } from "../../../../../../components/ObjectReference/utils";
import { AttributeTypeDisplayOnList } from "../../../../../../components/ObjectReference/AttributeTypeDisplay";
import { spacingTokens } from "../../../../../../designTokens";
import { AccordionCard } from "../../../../../../components/AccordionCard/AccordionCard";
import { ToggleSwitchV2 } from "../../../../../../components/ToggleSwitch/ToggleSwitch";
import {
  TextBaseSemibold,
  TextSmRegular,
} from "../../../../../../components/Typography/TypographyV2";
import _ from "lodash";

export const TableHeaderSection = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 25px;
`;
export const HeadingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

export const HeaderWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  align-items: center;
`;

export const CheckBoxNoMargin = styled(CheckBoxNoLabel)`
  margin-right: 0;
`;

const localstrings = (t: (s: string) => string) => ({
  tooltipAddAttributeError: t(
    "You cannot add attributes to a system default collection"
  ),
  tooltipDeleteAttributeError: t(
    "You cannot remove attributes from a system default collection"
  ),
  tooltipDeleteCollectionError: t(
    "You cannot remove a system default collection"
  ),
  tooltipEditTitleError: t("You cannot edit a system default collection"),
  tooltipDeleteTemplateInUseError: t(
    "Products are using the template, please duplicate the template for any archival changes"
  ),
  tooltipDeleteProfileTemplateInUseError: t(
    "Profiles are using the template, please duplicate the template for any archival changes"
  ),
  tooltipEditTitlePermissionError: t(
    "You don't have permission to edit this title"
  ),
  tooltipDeleteAttributeInUseError: t(
    "Cannot delete attribute, it is currently in use"
  ),
});

const removeAttributePatchBody = (
  uuid: UUID,
  collection: CollectionSchema
): CollectionPatchArgsSchema => {
  const { name, display_name, description, columns, rows } = collection;
  return {
    name,
    display_name: display_name ?? "",
    description: description ?? "",
    columns: [...columns.map(attributeToCollectionColumnCreation)],
    columns_to_delete: [uuid],
    rows: rows ? [...rows.map(collectionRowToCollectionRowCreation)] : [],
    rows_to_delete: [],
  };
};
const isLastAttribute = (collection: AttributeSchema[]) =>
  collection.length === 1;

type CollectionAttributeRow = {
  uuid: UUID;
  attribute_name: string;
  attribute_type: string;
  source_object_type?: string;
  target_object_type?: string;
  attribute_display_name: string;
  restricted: boolean;
  mandatory?: boolean;
  object_type?: string;
};

const MandatoryHeader = React.memo(function MandatoryHeader({
  isLoading,
  isChecked,
  onHeaderChange,
}: {
  isLoading: boolean;
  isChecked: boolean;
  onHeaderChange: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
}) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <HeaderWrapper>
      <>
        {isLoading ? (
          <LoadingIcon width={16} height={18} />
        ) : (
          <CheckBoxNoMargin
            name="mandatory-all"
            checked={isChecked}
            onChange={onHeaderChange}
          />
        )}
      </>
      {t("Mandatory")}
      <span
        data-tip={t(
          "Checking the box will make the attribute mandatory. Mandatory attributes must be filled in before a product can be published."
        )}
        data-for="mandatory-header-tip"
      >
        <InfoIcon fill={theme.secondaryIconColor} width={14} height={14} />
      </span>
      <ReactTooltip
        id="mandatory-header-tip"
        delayHide={500}
        clickable
        effect="solid"
      />
    </HeaderWrapper>
  );
});

const RestrictedHeaderComponent = React.memo(
  function RestrictedHeaderComponent({
    isLoading,
    isChecked,
    onHeaderChange,
  }: {
    isLoading: boolean;
    isChecked: boolean;
    onHeaderChange: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  }) {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
      <HeaderWrapper>
        <>
          {isLoading ? (
            <LoadingIcon width={16} height={18} />
          ) : (
            <CheckBoxNoMargin
              name="restricted-all"
              checked={isChecked}
              onChange={onHeaderChange}
            />
          )}
        </>
        {t("Restricted")}
        <span
          data-tip={t("Checking the box will restrict the attribute.")}
          data-for="restricted-header-tip"
        >
          <InfoIcon fill={theme.secondaryIconColor} width={14} height={14} />
        </span>
        <ReactTooltip
          id="restricted-header-tip"
          delayHide={500}
          clickable
          effect="solid"
        />
      </HeaderWrapper>
    );
  }
);

const RestrictedCell = React.memo(function RestrictedCell({
  getValue,
  row,
  onUpdate,
}: {
  getValue: () => boolean;
  row: { original: { uuid: string; object_type?: string } };
  onUpdate: (uuid: string, isChecked: boolean) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const uuid = row.original.uuid;
  const shouldHideCheckbox =
    row.original.object_type === "product_name" ||
    row.original.object_type === "product_id";

  if (shouldHideCheckbox) {
    return null;
  }

  const handleCheckBoxChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    try {
      await onUpdate(uuid, e.target.checked);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <span style={{ margin: "3px 15px 3px 0" }}>
          <LoadingIcon width={16} height={18} />
        </span>
      ) : (
        <CheckBoxNoMargin
          name={`restricted-${uuid}`}
          checked={getValue()}
          onChange={handleCheckBoxChange}
        />
      )}
    </>
  );
});

const MandatoryCell = React.memo(function MandatoryCell({
  getValue,
  row,
  onUpdate,
}: {
  getValue: () => boolean;
  row: { original: { uuid: string } };
  onUpdate: (uuid: string, isChecked: boolean) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const uuid = row.original.uuid;

  const handleCheckBoxChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    try {
      await onUpdate(uuid, e.target.checked);
    } finally {
      setIsLoading(false);
    }
  };

  return isLoading ? (
    <span style={{ margin: "3px 15px 3px 0" }}>
      <LoadingIcon width={16} height={18} />
    </span>
  ) : (
    <CheckBoxNoMargin
      name={`mandatory-${uuid}`}
      checked={getValue()}
      onChange={handleCheckBoxChange}
    />
  );
});

export const SellerAdminAttributesCollectionDetails = () => {
  const {
    tenant_id,
    storefront_metadata: { object_reference_enabled },
  } = useStoreState();
  const { t } = useTranslation();
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();
  const [isSearchable, setIsSearchable] = useState(false);
  const [isFilterable, setIsFilterable] = useState(false);
  const [applyfilters, setApplyfilters] = useState(false);
  const [filterableColumnIds, setFilterableColumnIds] = useState<string[]>([]);
  const [pendingFilterableColumnIds, setPendingFilterableColumnIds] = useState<
    string[]
  >([]);
  // Tracks the last value that was confirmed by the server. Used to distinguish
  // "user has pending edits" from "no edits yet" when a background SWR
  // revalidation fires so that in-progress debounced changes are never silently
  // overwritten.
  const committedFilterableIdsRef = useRef<string[]>([]);
  const hasMultipleLanguages = useHasMultipleLanguages();
  const { id: collection_id, template_id } =
    useParams<{ id: string; template_id?: string }>();
  const theme = useTheme();
  const [isMandatoryHeaderLoading, setIsMandatoryHeaderLoading] =
    useState(false);
  const [isRestrictedHeaderLoading, setIsRestrictedHeaderLoading] =
    useState(false);
  const location = useLocation();
  const [query] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });

  const { adminPath } = useRoutePath();
  const [tableData, setTableData] = useState<CollectionAttributeRow[]>([]);
  const [
    showAddAttributeToCollectionForm,
    setShowAddAttributeToCollectionForm,
  ] = useState(false);

  const editTitleMethodsOfUseForm = useFormWrapper({
    resolver: zodResolver(
      z.object({
        title: zodRequiredString(t),
        type: z.enum(["name"]),
      })
    ),
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const editDisplayNameMethodsOfUseForm = useFormWrapper({
    resolver: zodResolver(
      z.object({
        title: zodRequiredString(t),
        type: z.enum(["display_name"]),
      })
    ),
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const { hasPermission } = useAuthContext();

  const isInTemplateSection = !!template_id;

  const canEditTitle =
    (isInTemplateSection && hasPermission("modify_templates")) ||
    (!isInTemplateSection && hasPermission("modify_collections"));

  const canDeleteAttribute = isInTemplateSection
    ? hasPermission("delete_templates")
    : hasPermission("delete_collections");

  const canRemoveCollection = canDeleteAttribute;

  const { notifyError, notifySuccess } = useContext(Notifications);
  const history = useHistory();
  const titleRef = useRef<{ setEditMode: (editMode: boolean) => void }>(null);
  const displayNameRef =
    useRef<{ setEditMode: (editMode: boolean) => void }>(null);

  const {
    data: attributeCollectionResponse,
    error: attributesCollectionError,
    mutate: mutateCollection,
  } = useSWR<CollectionSchema & { translations: TranslationsSchema[] }>([
    template_id
      ? endpoints.v2_tenants_id_pim_templates_collections_id(
          tenant_id,
          template_id,
          collection_id
        )
      : endpoints.v2_tenants_id_pim_collections_id(tenant_id, collection_id),
    useMemo(() => ({ params: { include_translations: true } }), []),
  ]);
  const { data: template, mutate: mutateTemplate } = useSWR<
    AttributeTemplateSchema,
    AxiosError
  >(
    template_id ? `/v2/tenants/${tenant_id}/pim/templates/${template_id}` : null
  );

  const showMandatoryHeader = template?.show_mandatory_completion_score;
  const isProductTemplate =
    template?.template_type === "product" || template?.template_type === null;
  const templateSpecificTooltip = isProductTemplate
    ? localstrings(t).tooltipDeleteTemplateInUseError
    : localstrings(t).tooltipDeleteProfileTemplateInUseError;
  const isLoadingAttributes =
    !attributeCollectionResponse && !attributesCollectionError;

  const buildAttributeTableRows = useCallback(
    (collection: CollectionSchema): CollectionAttributeRow[] =>
      collection.columns
        .filter((attr) =>
          shouldShowObjectReferenceAttribute({
            inputType: attr.input_type,
            objectReferenceEnabled: object_reference_enabled,
          })
        )
        .map((attr) => ({
          uuid: attr.id,
          attribute_name: attr.name,
          attribute_type: attr.input_type,
          source_object_type: (attr as { source_object_type?: string })
            .source_object_type,
          target_object_type: (attr as { target_object_type?: string })
            .target_object_type,
          attribute_display_name: attr?.display_name
            ? t([attr.display_name])
            : "",
          restricted: attr.is_restricted,
          mandatory: showMandatoryHeader
            ? attr.is_mandatory ?? false
            : undefined,
          object_type: attr.object_type,
          is_system: attr.is_system,
        })),
    [object_reference_enabled, showMandatoryHeader, t]
  );

  useEffect(() => {
    if (attributeCollectionResponse) {
      setTableData(buildAttributeTableRows(attributeCollectionResponse));
      setIsSearchable(attributeCollectionResponse.enable_collection_search);
      setIsFilterable(attributeCollectionResponse.enable_collection_filter);

      const serverFilterableIds = attributeCollectionResponse.columns
        .filter((column) => column.is_filter_eligible)
        .map((column) => column.id);

      setFilterableColumnIds(serverFilterableIds);

      // Capture the previous committed value BEFORE updating the ref.
      // The functional updater below is queued by React and runs during the
      // next render — by which point committedFilterableIdsRef.current would
      // already hold the new server value if we updated it first, causing the
      // isEqual check to always return false on initial load and triggering a
      // spurious PATCH with an empty array.
      const previousCommittedIds = committedFilterableIdsRef.current;
      committedFilterableIdsRef.current = serverFilterableIds;

      // Only overwrite pending edits when the user has no in-progress changes
      // (pending still matches the PREVIOUS server-confirmed value). Background
      // SWR revalidations triggered by unrelated PATCHes must not silently
      // discard an ongoing debounced checkbox edit.
      setPendingFilterableColumnIds((prev) =>
        _.isEqual(prev, previousCommittedIds) ? serverFilterableIds : prev
      );
    }
  }, [attributeCollectionResponse, buildAttributeTableRows]);

  const tableColumns = useMemo<ColumnDef<CollectionAttributeRow>[]>(() => {
    const handleRemoveAttributeFromCollection = async (uuid: UUID) => {
      try {
        await Axios.patch<CollectionPatchArgsSchema, CollectionSchema>(
          endpoints.v2_tenants_id_pim_collections_id(tenant_id, collection_id),
          removeAttributePatchBody(uuid, attributeCollectionResponse!)
        );
        notifySuccess(t("Attribute removed successfully"));
        if (isLastAttribute(attributeCollectionResponse!.columns)) {
          notifySuccess(t("Collection removed successfully"));
          isInTemplateSection
            ? history.push(
                `${adminPath}/pim/templates/${template_id}/collections`
              )
            : history.push(`${adminPath}/pim/attributes/collections`);
        } else {
          mutateCollection();
        }
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage
            ? errorMessage
            : t("Failed to remove attribute from collection"),
          {
            error,
          }
        );
      }
    };

    const isAttributesInUse = () => !!attributeCollectionResponse?.rows?.length;

    return isInTemplateSection
      ? [
          {
            header: t("Attribute Name"),
            accessorKey: "attribute_name",
          },
          {
            header: () => (
              <div style={{ textAlign: "left" }}>{t("Attribute Type")}</div>
            ),
            accessorKey: "attribute_type",
            cell: ({
              getValue,
              row,
            }: {
              getValue: Getter<string>;
              row: {
                original: {
                  attribute_type: string;
                  source_object_type?: string;
                  target_object_type?: string;
                };
              };
            }) => (
              <div style={{ textAlign: "left" }}>
                {toTitleCase(removeUnderscore(getValue() as string))}
                {row.original.attribute_type === "object_reference" && (
                  <AttributeTypeDisplayOnList
                    sourceObjectType={row.original.source_object_type}
                    targetObjectType={row.original.target_object_type}
                  />
                )}
              </div>
            ),
          },
          {
            header: t("Attribute Display Name"),
            accessorKey: "attribute_display_name",
          },
          {
            header: () => (
              <RestrictedHeaderComponent
                isLoading={isRestrictedHeaderLoading}
                isChecked={attributeCollectionResponse?.is_restricted ?? false}
                onHeaderChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const isChecked = e.target.checked;
                  setIsRestrictedHeaderLoading(true);
                  try {
                    await Axios.patch<
                      { attribute_ids: "all" | string[] },
                      AttributeTemplateSchema
                    >(
                      endpoints.v2_tenants_id_or_slug_pim_templates_id_collections_id_attributes_restricted(
                        tenant_id,
                        template!.template_id,
                        attributeCollectionResponse!.id
                      ),
                      isChecked
                        ? { attributes_to_restrict: "all" }
                        : { attributes_to_unrestrict: "all" }
                    );
                    setTableData((prevData) =>
                      prevData.map((row) => ({
                        ...row,
                        restricted: isChecked,
                      }))
                    );
                    await mutateTemplate();
                    await mutateCollection();
                  } catch (error) {
                    const errorMessage = (error as AxiosError)?.response?.data
                      ?.message;
                    notifyError(
                      errorMessage
                        ? errorMessage
                        : t(
                            "Could not update restricted status. Please try again later."
                          ),
                      { error }
                    );
                  } finally {
                    setIsRestrictedHeaderLoading(false);
                  }
                }}
              />
            ),
            accessorKey: "restricted",
            enableSorting: false,
            cell: (props) => (
              <RestrictedCell
                getValue={() => props.getValue() as boolean}
                row={props.row}
                onUpdate={async (uuid, isChecked) => {
                  const patchRequestBody = isChecked
                    ? { attributes_to_restrict: [uuid] }
                    : { attributes_to_unrestrict: [uuid] };
                  try {
                    await Axios.patch<
                      {
                        attributes_to_restrict?: string[];
                        attributes_to_unrestrict?: string[];
                      },
                      AttributeTemplateSchema
                    >(
                      endpoints.v2_tenants_id_or_slug_pim_templates_id_collections_id_attributes_restricted(
                        tenant_id,
                        template!.template_id,
                        attributeCollectionResponse!.id
                      ),
                      patchRequestBody
                    );
                    setTableData((prevData) =>
                      prevData.map((row) =>
                        row.uuid === uuid
                          ? { ...row, restricted: isChecked }
                          : row
                      )
                    );
                  } catch (error) {
                    await mutateCollection();
                    const errorMessage = (error as AxiosError)?.response?.data
                      ?.message;
                    notifyError(
                      errorMessage
                        ? errorMessage
                        : t(
                            "Could not update restricted status. Please try again later."
                          ),
                      { error }
                    );
                  }
                }}
              />
            ),
          },
          ...(showMandatoryHeader
            ? [
                {
                  header: () => (
                    <MandatoryHeader
                      isLoading={isMandatoryHeaderLoading}
                      isChecked={
                        attributeCollectionResponse?.is_mandatory ?? false
                      }
                      onHeaderChange={async (
                        e: ChangeEvent<HTMLInputElement>
                      ) => {
                        const isChecked = e.target.checked;
                        setIsMandatoryHeaderLoading(true);
                        try {
                          await Axios.patch<
                            { attribute_ids: "all" | string[] },
                            AttributeTemplateSchema
                          >(
                            endpoints.v2_tenants_id_or_slug_pim_templates_id_collections_id_attributes_mandate(
                              tenant_id,
                              template!.template_id,
                              attributeCollectionResponse!.id
                            ),
                            isChecked
                              ? { attributes_to_mandate: "all" }
                              : { attributes_to_unmandate: "all" }
                          );
                          setTableData((prevData) =>
                            prevData.map((row) => ({
                              ...row,
                              mandatory: isChecked,
                            }))
                          );
                        } catch (error) {
                          await mutateCollection();
                          const errorMessage = (error as AxiosError)?.response
                            ?.data?.message;
                          notifyError(
                            errorMessage
                              ? errorMessage
                              : t(
                                  "Could not update mandatory status. Please try again later."
                                ),
                            { error }
                          );
                        } finally {
                          setIsMandatoryHeaderLoading(false);
                        }
                      }}
                    />
                  ),
                  accessorKey: "mandatory",
                  enableSorting: false,
                  cell: (props: {
                    getValue: Getter<boolean>;
                    row: { original: { uuid: string } };
                  }) => (
                    <MandatoryCell
                      getValue={() => props.getValue() as boolean}
                      row={props.row}
                      onUpdate={async (uuid, isChecked) => {
                        const patchRequestBody = isChecked
                          ? { attributes_to_mandate: [uuid] }
                          : { attributes_to_unmandate: [uuid] };
                        try {
                          await Axios.patch<
                            {
                              attributes_to_mandate?: string[];
                              attributes_to_unmandate?: string[];
                            },
                            AttributeTemplateSchema
                          >(
                            endpoints.v2_tenants_id_or_slug_pim_templates_id_collections_id_attributes_mandate(
                              tenant_id,
                              template!.template_id,
                              attributeCollectionResponse!.id
                            ),
                            patchRequestBody
                          );
                          setTableData((prevData) =>
                            prevData.map((row) =>
                              row.uuid === uuid
                                ? { ...row, mandatory: isChecked }
                                : row
                            )
                          );
                        } catch (error) {
                          await mutateCollection();
                          const errorMessage = (error as AxiosError)?.response
                            ?.data?.message;
                          notifyError(
                            errorMessage
                              ? errorMessage
                              : t(
                                  "Could not update mandatory status. Please try again later."
                                ),
                            { error }
                          );
                        }
                      }}
                    />
                  ),
                },
              ]
            : []),
          {
            // The empty header and accessor are needed so it doesn't crash.
            header: "",
            accessorKey: " ",
            cell: ({
              row: { original },
            }: {
              row: { original: { uuid: string; is_system?: boolean } };
            }) =>
              canDeleteAttribute ? (
                <ButtonWithConfirmDialog
                  Button={(props) => (
                    <>
                      <DeleteButton
                        {...props}
                        datafor="attribute-delete-button"
                        datatip={
                          isSystemProtectedAttribute(
                            attributeCollectionResponse?.is_system,
                            original.is_system
                          )
                            ? localstrings(t).tooltipDeleteAttributeError
                            : isInTemplateSection && isAttributesInUse()
                            ? t(
                                "Cannot delete attribute, it is currently in use"
                              )
                            : template?.is_frozen
                            ? templateSpecificTooltip
                            : ""
                        }
                      />
                      <ReactTooltip id="attribute-delete-button" />
                    </>
                  )}
                  testid={"remove-individiual-attribute-from-collection"}
                  disabled={
                    isSystemProtectedAttribute(
                      attributeCollectionResponse?.is_system,
                      original.is_system
                    ) ||
                    isAttributesInUse() ||
                    template?.is_frozen
                  }
                  handleConfirm={() => {
                    handleRemoveAttributeFromCollection(original.uuid);
                  }}
                  confirmMessage={
                    isLastAttribute(attributeCollectionResponse?.columns ?? [])
                      ? t(
                          "Are you sure you want to remove this attribute? This collection will be removed."
                        )
                      : t("Are you sure you want to remove this attribute?")
                  }
                  title={t("Remove Attribute")}
                  confirmBtnLabel={t("Remove")}
                  confirmBtnType="destructive"
                  useV2Buttons={true}
                />
              ) : null,
          },
        ]
      : [
          {
            header: t("Attribute Name"),
            accessorKey: "attribute_name",
          },
          {
            header: () => (
              <div style={{ textAlign: "left" }}>{t("Attribute Type")}</div>
            ),
            accessorKey: "attribute_type",
            cell: ({
              getValue,
              row,
            }: {
              getValue: Getter<string>;
              row: {
                original: {
                  attribute_type: string;
                  source_object_type?: string;
                  target_object_type?: string;
                };
              };
            }) => (
              <div style={{ textAlign: "left" }}>
                {toTitleCase(removeUnderscore(getValue() as string))}
                {row.original.attribute_type === "object_reference" && (
                  <AttributeTypeDisplayOnList
                    sourceObjectType={row.original.source_object_type}
                    targetObjectType={row.original.target_object_type}
                  />
                )}
              </div>
            ),
          },
          {
            header: t("Attribute Display Name"),
            accessorKey: "attribute_display_name",
          },
          {
            // The empty header and accessor are needed so it doesn't crash.
            header: "",
            accessorKey: " ",
            cell: ({
              row: { original },
            }: {
              row: { original: { uuid: string; is_system?: boolean } };
            }) =>
              canDeleteAttribute ? (
                <ButtonWithConfirmDialog
                  Button={(props) => (
                    <>
                      <DeleteButton
                        {...props}
                        datafor="attribute-delete-button"
                        datatip={
                          isSystemProtectedAttribute(
                            attributeCollectionResponse?.is_system,
                            original.is_system
                          )
                            ? localstrings(t).tooltipDeleteAttributeError
                            : isInTemplateSection && isAttributesInUse()
                            ? t(
                                "Cannot delete attribute, it is currently in use"
                              )
                            : template?.is_frozen
                            ? templateSpecificTooltip
                            : ""
                        }
                      />
                      <ReactTooltip id="attribute-delete-button" />
                    </>
                  )}
                  testid={"remove-individiual-attribute-from-collection"}
                  disabled={
                    isSystemProtectedAttribute(
                      attributeCollectionResponse?.is_system,
                      original.is_system
                    ) ||
                    isAttributesInUse() ||
                    template?.is_frozen
                  }
                  handleConfirm={() => {
                    handleRemoveAttributeFromCollection(original.uuid);
                  }}
                  confirmMessage={
                    isLastAttribute(attributeCollectionResponse?.columns ?? [])
                      ? t(
                          "Are you sure you want to remove this attribute? This collection will be removed."
                        )
                      : t("Are you sure you want to remove this attribute?")
                  }
                  title={t("Remove Attribute")}
                  confirmBtnLabel={t("Remove")}
                  confirmBtnType="destructive"
                  useV2Buttons={true}
                />
              ) : null,
          },
        ];
  }, [
    isInTemplateSection,
    t,
    showMandatoryHeader,
    tenant_id,
    collection_id,
    attributeCollectionResponse,
    notifySuccess,
    history,
    adminPath,
    template_id,
    mutateCollection,
    notifyError,
    isRestrictedHeaderLoading,
    template,
    mutateTemplate,
    isMandatoryHeaderLoading,
    canDeleteAttribute,
    templateSpecificTooltip,
  ]);

  const handleEditCollectionSettings = useCallback(
    async (payload: {
      enable_collection_filter?: boolean;
      enable_collection_search?: boolean;
      filterable_column_ids?: string[];
    }) => {
      try {
        await Axios.patch<CollectionPatchArgsSchema, CollectionSchema>(
          endpoints.v2_tenants_id_pim_collections_id(tenant_id, collection_id),
          payload
        );
        notifySuccess(t("Collection settings updated successfully"));
      } catch (error) {
        mutateCollection();
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage
            ? errorMessage
            : t("There was an error editing the collection settings"),
          { error }
        );
      }
    },
    [tenant_id, collection_id, notifySuccess, notifyError, t, mutateCollection]
  );

  useEffect(() => {
    if (
      !_.isEqual(filterableColumnIds, pendingFilterableColumnIds) &&
      applyfilters
    ) {
      const delayApplyFilters = setTimeout(() => {
        handleEditCollectionSettings({
          filterable_column_ids: pendingFilterableColumnIds,
        });
        setApplyfilters(false);
      }, 1500);

      return () => clearTimeout(delayApplyFilters);
    }
  }, [
    filterableColumnIds,
    pendingFilterableColumnIds,
    handleEditCollectionSettings,
    applyfilters,
  ]);

  const reorderedRowsRef = useRef<CollectionAttributeRow[]>([]);
  const [isSavingReorder, setIsSavingReorder] = useState(false);

  const persistAttributesReorder = useCallback(async () => {
    setIsSavingReorder(true);
    const reorderList = reorderedRowsRef.current.map((row) => row.uuid);

    try {
      await Axios.post(
        `/v2/tenants/${tenant_id}/pim/collections/${collection_id}/priority?type=collection_column`,
        { items: reorderList }
      );
      notifySuccess(t("Your changes have been saved successfully"));
      mutateCollection();
    } catch (error) {
      notifyError(t("Something went wrong, please try again"), { error });
      if (attributeCollectionResponse) {
        setTableData(buildAttributeTableRows(attributeCollectionResponse));
      }
    } finally {
      setIsSavingReorder(false);
    }
  }, [
    tenant_id,
    collection_id,
    notifySuccess,
    notifyError,
    t,
    mutateCollection,
    attributeCollectionResponse,
    buildAttributeTableRows,
  ]);

  const { handleRowReorder, handleDrop: handleAttributesReorder } =
    useDebouncedRowReorder(reorderedRowsRef, persistAttributesReorder);

  if (isLoadingAttributes) {
    return <DelayedSpinner />;
  }

  if (attributesCollectionError) {
    return (
      <ErrorPlaceholder
        message={t("There was an error loading the collection")}
      />
    );
  }

  if (attributeCollectionResponse) {
    const handleTitleEditConfirm = async ({
      title,
      type,
    }: {
      title: string;
      type: "name" | "display_name";
    }) => {
      const canSubmit = () =>
        (type === "name" &&
          title.trim() !== attributeCollectionResponse.name) ||
        (type === "display_name" &&
          title.trim() !== attributeCollectionResponse.display_name);

      if (canSubmit()) {
        const { rows } = attributeCollectionResponse;
        try {
          await Axios.patch<CollectionPatchArgsSchema, CollectionSchema>(
            endpoints.v2_tenants_id_pim_collections_id(
              tenant_id,
              collection_id
            ),
            type === "name"
              ? {
                  name: title.trim(),
                  description: attributeCollectionResponse.description ?? "",
                  rows: rows
                    ? [...rows.map(collectionRowToCollectionRowCreation)]
                    : [],
                }
              : {
                  display_name: title.trim(),
                  description: attributeCollectionResponse.description ?? "",
                  rows: rows
                    ? [...rows.map(collectionRowToCollectionRowCreation)]
                    : [],
                }
          );
          // success notification intentionally left out because this is "inline"
          if (type === "name") {
            titleRef.current?.setEditMode(false);
          } else {
            displayNameRef.current?.setEditMode(false);
          }
        } catch (error) {
          const errorMessage = (error as AxiosError)?.response?.data?.message;
          notifyError(
            errorMessage ? errorMessage : t("Error editing collection title"),
            {
              error,
            }
          );
        }
        mutateCollection();
      }
    };

    const handleRemoveCollection = async () => {
      try {
        await Axios.delete(
          endpoints.v2_tenants_id_pim_collections_id(tenant_id, collection_id)
        );
        notifySuccess(t("Collection removed successfully"));

        isInTemplateSection
          ? history.push(
              `${adminPath}/pim/templates/${template_id}/collections`
            )
          : history.push(`${adminPath}/pim/attributes/collections`);
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage
            ? errorMessage
            : t("There was an error deleting the collection"),
          {
            error,
          }
        );
      }
    };

    const isFilterableRowAttrType = (row: CollectionAttributeRow) =>
      row.attribute_type === "single_select" ||
      row.attribute_type === "multi_select" ||
      row.attribute_type === "toggle" ||
      row.attribute_type === "user" ||
      row.attribute_type === "numeric";

    const params = new URLSearchParams();
    if (query.q) {
      params.append("q", query.q);
    }
    params.append("offset", String(query?.offset ?? 0));
    params.append("perPage", String(query?.perPage ?? 10));

    return (
      <PageWrapper>
        <Breadcrumb
          style={{ padding: `${spacingTokens.spacing_xl}`, paddingBottom: 0 }}
        >
          {isInTemplateSection ? (
            <>
              <LinkUnstyled to={`${adminPath}/pim/templates`}>
                {t("Templates")}
              </LinkUnstyled>
              <div>/</div>
              <LinkUnstyled
                to={`${adminPath}/pim/templates/${template_id}/collections`}
              >
                {template ? getTemplateDisplayName(template) : t("Template")}
              </LinkUnstyled>
            </>
          ) : (
            <LinkUnstyled
              to={`${adminPath}/pim/attributes/collections?${params}`}
            >
              {t("Collections")}
            </LinkUnstyled>
          )}
        </Breadcrumb>
        <TemplatesNavWithPadding
          style={{
            marginBottom: spacingTokens.spacing_xl,
          }}
        >
          {isInTemplateSection ? (
            <TemplatesNav
              pageTitle={template?.template_name ?? ""}
              tabIndex={1}
              templateID={template_id!}
              enableCustomForms={template?.enable_custom_forms}
            />
          ) : (
            <AttributesNav tabName="Collections" params={params} />
          )}
        </TemplatesNavWithPadding>
        <div
          style={{
            width: "fit-content",
            padding: `0 ${spacingTokens.spacing_xl}`,
          }}
        >
          <Link
            to={
              location.search.includes("from=dashboard")
                ? `${adminPath}/dashboard`
                : isInTemplateSection
                ? `${adminPath}/pim/templates/${template_id}/collections?${params}`
                : `${adminPath}/pim/attributes/collections?${params}`
            }
          >
            <LinkButtonLargeV2 style={{ padding: "0 0 20px" }}>
              <CaretLeft fill="currentColor" />
              {t("Collections")}
            </LinkButtonLargeV2>
          </Link>
        </div>
        <PageContentWrapper>
          <WhiteInnerCard
            style={{ marginBottom: theme.resolve("spacing_3xl") }}
          >
            <AccordionCard
              title={t("Collection Definition")}
              hideHeaderBorder={true}
              actionChildren={() => (
                <div style={{ alignSelf: "flex-end" }}>
                  {attributeCollectionResponse.is_system && (
                    <Flex
                      style={{
                        justifyContent: "end",
                        alignItems: "center",
                        flexWrap: "nowrap",
                        margin: `0 0 ${spacingTokens.spacing_md}`,
                      }}
                    >
                      <SystemIconWrapper
                        data-for={`system${collection_id}`}
                        data-tip={t("System Default")}
                      >
                        <SystemDefaultIcon width={22} height={22} />
                        <ReactTooltip id={`system${collection_id}`} />
                      </SystemIconWrapper>
                      <SoftHeader2 style={{ whiteSpace: "nowrap" }}>
                        {t("System Default")}
                      </SoftHeader2>
                    </Flex>
                  )}
                </div>
              )}
              expanded={true}
            >
              <ContentWrapper>
                <WhiteInnerCard
                  style={{ marginTop: theme.resolve("spacing_xl") }}
                >
                  <AccordionCard
                    hideHeaderBorder={true}
                    title={() =>
                      isInTemplateSection ? (
                        <>
                          <H3 style={{ margin: 0 }}>
                            {attributeCollectionResponse.name}
                          </H3>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: theme.resolve("spacing_xs"),
                            }}
                          >
                            <TextSmRegular color="secondary">
                              {t("Display name:")}
                            </TextSmRegular>
                            <TextSmRegular color="primary">
                              {t([attributeCollectionResponse.display_name])}
                            </TextSmRegular>
                          </div>
                        </>
                      ) : (
                        <>
                          <form
                            id="edit-name-form"
                            noValidate
                            onSubmit={editTitleMethodsOfUseForm.handleSubmit(
                              handleTitleEditConfirm
                            )}
                          >
                            <EditableTitle
                              title={attributeCollectionResponse.name}
                              formId="edit-name-form"
                              name="title"
                              methodsOfUseForm={editTitleMethodsOfUseForm}
                              disabled={
                                attributeCollectionResponse.is_system ||
                                !hasPermission("modify_collections")
                              }
                              datatip={
                                attributeCollectionResponse.is_system
                                  ? localstrings(t).tooltipEditTitleError
                                  : !canEditTitle
                                  ? localstrings(t)
                                      .tooltipEditTitlePermissionError
                                  : ""
                              }
                              ref={titleRef}
                            />
                            <input
                              name="type"
                              ref={editTitleMethodsOfUseForm.register}
                              value="name"
                              type="hidden"
                            />
                          </form>
                          <form
                            id="edit-displayname-form"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                            noValidate
                            onSubmit={editDisplayNameMethodsOfUseForm.handleSubmit(
                              handleTitleEditConfirm
                            )}
                          >
                            <SoftHeaderMedium>
                              {t("Display name: ")}
                            </SoftHeaderMedium>
                            <EditableTitle
                              title={
                                attributeCollectionResponse.display_name ?? "--"
                              }
                              formId="edit-displayname-form"
                              name="title"
                              methodsOfUseForm={editDisplayNameMethodsOfUseForm}
                              disabled={!hasPermission("modify_collections")}
                              datatip={
                                !hasPermission("modify_collections")
                                  ? localstrings(t)
                                      .tooltipEditTitlePermissionError
                                  : ""
                              }
                              ref={displayNameRef}
                              fontSize="regular"
                              fontWeight="regular"
                            />
                            <input
                              name="type"
                              ref={editDisplayNameMethodsOfUseForm.register}
                              value="display_name"
                              type="hidden"
                            />
                          </form>
                        </>
                      )
                    }
                    actionChildren={() => <></>}
                    expanded={true}
                  >
                    <div style={{ marginTop: theme.resolve("spacing_lg") }}>
                      <div style={{ position: "relative" }}>
                        <Table
                          isLoading={isLoadingAttributes}
                          error={attributesCollectionError}
                          columns={tableColumns}
                          data={tableData}
                          rowHover={rowHover}
                          enableReorder={
                            isInTemplateSection && !isSavingReorder
                          }
                          handleRowReorder={handleRowReorder}
                          handleDrop={handleAttributesReorder}
                        />
                      </div>
                    </div>
                    {hasPermission("modify_collections") && (
                      <>
                        <TertiaryButtonMediumV2
                          onClick={() =>
                            setShowAddAttributeToCollectionForm(true)
                          }
                          style={{ marginTop: theme.resolve("spacing_xl") }}
                        >
                          <PlusIcon
                            width={20}
                            height={20}
                            fill={theme.primaryButtonTextColor}
                          />

                          {t("Add attribute")}
                        </TertiaryButtonMediumV2>
                        {hasMultipleLanguages && !isInTemplateSection ? (
                          <div style={{ marginTop: "32px" }}>
                            <CollectionTranslations
                              collection={attributeCollectionResponse}
                              mutate={mutateCollection}
                            />
                          </div>
                        ) : (
                          <></>
                        )}
                      </>
                    )}
                  </AccordionCard>
                </WhiteInnerCard>
                {isInTemplateSection && (
                  <>
                    <WhiteInnerCard
                      style={{
                        marginTop: theme.resolve("spacing_xl"),
                        marginBottom: 0,
                      }}
                    >
                      <AccordionCard
                        title={() => (
                          <TextBaseSemibold>{t("Items")}</TextBaseSemibold>
                        )}
                        actionChildren={() => <></>}
                        hideHeaderBorder={true}
                      >
                        <div style={{ marginTop: theme.resolve("spacing_lg") }}>
                          <TemplateCollectionItems
                            template={template}
                            error={attributesCollectionError}
                            isLoading={isLoadingAttributes}
                            collection={attributeCollectionResponse}
                            onSuccess={() => mutateCollection()}
                          />
                        </div>
                      </AccordionCard>
                    </WhiteInnerCard>
                  </>
                )}
                <SlideOut
                  show={showAddAttributeToCollectionForm}
                  closeFlyout={() => setShowAddAttributeToCollectionForm(false)}
                  title={t("Add Attributes")}
                >
                  <AddAttributeToExistingCollectionForm
                    collection={attributeCollectionResponse}
                    isTemplateInUse={template?.is_frozen}
                    templateEntityType={
                      template_id
                        ? template?.template_type ?? "product"
                        : undefined
                    }
                    templateType={template?.template_type ?? null}
                    onSuccess={() => {
                      mutateCollection();
                      setShowAddAttributeToCollectionForm(false);
                    }}
                    onCancel={() => setShowAddAttributeToCollectionForm(false)}
                  />
                </SlideOut>
              </ContentWrapper>
            </AccordionCard>
          </WhiteInnerCard>
          {isInTemplateSection && (
            <WhiteInnerCard
              style={{ marginBottom: theme.resolve("spacing_3xl") }}
            >
              <AccordionCard
                title={t("Collection Settings")}
                actionChildren={() => <></>}
                expanded={true}
                hideHeaderBorder={true}
              >
                <div style={{ marginTop: theme.resolve("spacing_lg") }}>
                  {attributeCollectionResponse.has_reference_entity_mapping && (
                    <WhiteInnerCard
                      style={{ marginBottom: theme.resolve("spacing_2xl") }}
                    >
                      <AccordionCard
                        title={() => (
                          <TextBaseSemibold>
                            {t("Reference Entity Mapping")}
                          </TextBaseSemibold>
                        )}
                        actionChildren={() => <></>}
                        hideHeaderBorder={true}
                      >
                        <ReferenceEntityMappingTable
                          contextType="collection"
                          contextId={collection_id}
                          showTitle={false}
                        />
                      </AccordionCard>
                    </WhiteInnerCard>
                  )}
                  <WhiteInnerCard style={{ marginBottom: 0 }}>
                    <AccordionCard
                      title={() => (
                        <TextBaseSemibold>
                          {t("Search & Filter")}
                        </TextBaseSemibold>
                      )}
                      actionChildren={() => <></>}
                      hideHeaderBorder={true}
                    >
                      <div style={{ marginTop: theme.resolve("spacing_lg") }}>
                        <WhiteInnerCard
                          style={{ padding: theme.resolve("spacing_lg") }}
                        >
                          <ToggleSwitchV2
                            label={t("Search")}
                            checked={isSearchable}
                            name="search"
                            onChange={(e) => {
                              setIsSearchable(e.target.checked);
                              handleEditCollectionSettings({
                                enable_collection_search: e.target.checked,
                              });
                            }}
                          />
                          <TextSmRegular
                            color="secondary"
                            style={{ marginLeft: theme.resolve("spacing_6xl") }}
                          >
                            {t(
                              "Enable keyword search across all searchable attributes"
                            )}
                          </TextSmRegular>
                        </WhiteInnerCard>
                        <WhiteInnerCard
                          style={{
                            padding: theme.resolve("spacing_lg"),
                            marginBottom: 0,
                          }}
                        >
                          {" "}
                          <ToggleSwitchV2
                            label={t("Filter")}
                            checked={isFilterable}
                            name="filter"
                            onChange={(e) => {
                              setIsFilterable(e.target.checked);
                              handleEditCollectionSettings({
                                enable_collection_filter: e.target.checked,
                              });
                            }}
                          />
                          <TextSmRegular
                            color="secondary"
                            style={{ marginLeft: theme.resolve("spacing_6xl") }}
                          >
                            {t(
                              "Enable filtering by attributes (Single select, Multi select, Toggle, Numeric and User)"
                            )}
                          </TextSmRegular>
                          {isFilterable && (
                            <div
                              style={{ marginTop: theme.resolve("spacing_lg") }}
                            >
                              {tableData
                                .filter(isFilterableRowAttrType)
                                .map((row) => (
                                  <div
                                    key={row.uuid}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: theme.resolve("spacing_md"),
                                      marginBottom: theme.resolve("spacing_md"),
                                      marginLeft: theme.resolve("spacing_6xl"),
                                    }}
                                  >
                                    <CheckBoxNoMargin
                                      id={`filterable-column-${row.uuid}`}
                                      checked={pendingFilterableColumnIds?.includes(
                                        row.uuid
                                      )}
                                      onChange={(
                                        e: ChangeEvent<HTMLInputElement>
                                      ) => {
                                        setApplyfilters(true);
                                        setPendingFilterableColumnIds(
                                          e.target.checked
                                            ? [
                                                ...pendingFilterableColumnIds,
                                                row.uuid,
                                              ]
                                            : pendingFilterableColumnIds?.filter(
                                                (id) => id !== row.uuid
                                              )
                                        );
                                      }}
                                      name={row.uuid}
                                    />
                                    <TextSmRegular
                                      as="label"
                                      htmlFor={`filterable-column-${row.uuid}`}
                                      color="secondary"
                                      style={{ cursor: "pointer" }}
                                    >
                                      {row.attribute_name}
                                    </TextSmRegular>
                                  </div>
                                ))}
                            </div>
                          )}
                        </WhiteInnerCard>
                      </div>
                    </AccordionCard>
                  </WhiteInnerCard>
                </div>
              </AccordionCard>
            </WhiteInnerCard>
          )}
          {canRemoveCollection && !attributeCollectionResponse.is_system && (
            <ButtonWithConfirmDialog
              Button={(props) => (
                <>
                  <DestructiveButtonLargeV2
                    {...props}
                    style={{ marginBottom: theme.resolve("spacing_3xl") }}
                  >
                    {<DeleteIcon />} {t("Remove Collection")}
                  </DestructiveButtonLargeV2>
                  <ReactTooltip
                    id="remove-collection-button"
                    effect="solid"
                    place="right"
                  />
                </>
              )}
              disabled={
                attributeCollectionResponse.is_system || template?.is_frozen
              }
              testid="remove-collection"
              handleConfirm={handleRemoveCollection}
              datafor="remove-collection-button"
              useV2Buttons={true}
              title={t("Remove Collection")}
              datatip={
                attributeCollectionResponse.is_system
                  ? localstrings(t).tooltipDeleteCollectionError
                  : template?.is_frozen
                  ? templateSpecificTooltip
                  : ""
              }
              confirmMessage={t(
                "Are you sure you want to remove this collection?"
              )}
              confirmBtnLabel={t("Remove")}
              confirmBtnType="destructive"
            />
          )}
        </PageContentWrapper>
      </PageWrapper>
    );
  } else return null;
};
